/*
 * Collection storefront behaviour, shared by every section that renders
 * `snippets/collection-product-card.liquid`.
 *
 * The markup lives in two sections now — the Marine band with the featured
 * Gateway, and the product grid — so this cannot stay inline in one of them.
 * It initialises every `[data-cs-root]` on the page and keeps exactly ONE
 * toast, otherwise two sections would each announce the same add.
 *
 * Cart routes arrive as data attributes because an asset file gets no Liquid.
 */
(() => {
  if (window.__vmCollectionStorefront) return;
  window.__vmCollectionStorefront = true;

  const DEFAULT_STRINGS = {
    added: 'Added to cart',
    loading: 'Adding…',
    error: 'Something went wrong.',
    soldOut: 'Sold out',
    quantityLabel: 'Quantity',
    removed: 'Remove from cart',
    discountPercent: '__PCT__% off',
    discountShort: '−__PCT__%'
  };

  /* ---- Toast: tek örnek ------------------------------------------------
     İki bölüm de kendi toast'ını basar; ilkini kullanır, fazlasını DOM'dan
     çıkarırız. Aksi hâlde aynı ekleme iki kez duyurulur. */
  const toastState = {
    el: null,
    title: null,
    message: null,
    timer: null,
    remaining: 8000,
    startedAt: 0,
    hovered: false,
    focused: false,
    lastAddButton: null,
    strings: Object.assign({}, DEFAULT_STRINGS)
  };

  const hideToast = () => {
    const toast = toastState.el;
    if (!toast) return;
    clearTimeout(toastState.timer);
    toastState.timer = null;
    const hadFocus = toast.contains(document.activeElement);
    toast.hidden = true;
    toast.removeAttribute('data-error');
    const back = toastState.lastAddButton;
    if (hadFocus && back && document.contains(back) && !back.disabled) back.focus();
  };

  const startToastTimer = () => {
    clearTimeout(toastState.timer);
    toastState.timer = null;
    if (toastState.hovered || toastState.focused || !toastState.el || toastState.el.hidden) return;
    toastState.startedAt = Date.now();
    toastState.timer = setTimeout(hideToast, toastState.remaining);
  };

  const pauseToast = () => {
    if (!toastState.timer) return;
    clearTimeout(toastState.timer);
    toastState.timer = null;
    toastState.remaining = Math.max(1000, toastState.remaining - (Date.now() - toastState.startedAt));
  };

  const paintToast = (detail, isError) => {
    const s = toastState.strings;
    toastState.title.textContent = detail.productTitle || '';
    const variant = detail.variantTitle && detail.variantTitle !== 'Default Title' ? ` · ${detail.variantTitle}` : '';
    toastState.message.textContent = `${detail.message || (isError ? s.error : s.added)}${variant}`;
  };

  const showToast = (detail = {}, isError = false, announce = true) => {
    const toast = toastState.el;
    if (!toast) return;
    clearTimeout(toastState.timer);
    toastState.remaining = 8000;
    toast.hidden = false;
    toast.dataset.error = isError ? 'true' : 'false';
    /* Aynı şablonda product-wizard da kendi role="status" alanına "Sepete
       eklendi" yazıp cart:item-added yayınlıyor. Toast görünmeye devam etsin
       (sepet linkini taşıyor) ama ekran okuyucu aynı olayı iki kez duymasın:
       yabancı olaylarda canlı bölge kapanır. */
    toast.setAttribute('role', isError ? 'alert' : 'status');
    toast.setAttribute('aria-live', isError ? 'assertive' : announce ? 'polite' : 'off');
    /* Canlı bölge görünür hâle geldikten SONRA doldurulur; aynı görevde
       yapılırsa NVDA/VoiceOver değişikliği duyurmayabiliyor. */
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => paintToast(detail, isError));
    } else {
      paintToast(detail, isError);
    }
    toastState.hovered = toast.matches(':hover');
    toastState.focused = toast.contains(document.activeElement);
    startToastTimer();
  };

  const adoptToast = (root) => {
    const toast = root.querySelector('[data-cs-toast]');
    if (!toast) return;
    if (toastState.el && document.contains(toastState.el) && toastState.el !== toast) {
      toast.remove();
      return;
    }
    if (toastState.el === toast) return;
    toastState.el = toast;
    toastState.title = toast.querySelector('[data-cs-toast-title]');
    toastState.message = toast.querySelector('[data-cs-toast-message]');
    toast.querySelector('[data-cs-toast-close]')?.addEventListener('click', hideToast);
    toast.addEventListener('mouseenter', () => { toastState.hovered = true; pauseToast(); });
    toast.addEventListener('mouseleave', () => { toastState.hovered = false; startToastTimer(); });
    toast.addEventListener('focusin', () => { toastState.focused = true; pauseToast(); });
    toast.addEventListener('focusout', (event) => {
      if (!toast.contains(event.relatedTarget)) {
        toastState.focused = false;
        startToastTimer();
      }
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && toastState.el && !toastState.el.hidden) hideToast();
  });

  document.addEventListener('cart:item-added', (event) => {
    const detail = event.detail || {};
    showToast(detail, false, detail.source === 'collection-storefront');
  });

  /* ---- Görsel ---------------------------------------------------------- */
  const paintImage = (image, source) => {
    if (!image || !source?.dataset.image) return;
    if (image.currentSrc === source.dataset.image) return;
    /* Geçiş sınıfı: bant görseli yeni dosya gelene kadar söner, yüklenince
       açılır (CSS'i olmayan ızgara kartlarında etkisiz). Hata durumunda da
       kaldırılır ki görsel görünmez kalmasın. */
    image.classList.add('is-swapping');
    const settle = () => image.classList.remove('is-swapping');
    image.addEventListener('load', settle, { once: true });
    image.addEventListener('error', settle, { once: true });
    /* srcset de yenilenir: boşaltıp yalnız en büyük src'yi bırakmak telefonda
       her geçişte gereksiz büyük dosya indirtiyordu. */
    image.srcset = source.dataset.imageSrcset || '';
    image.src = source.dataset.image;
  };

  const markActiveDot = (card, url) => {
    card.querySelectorAll('[data-cs-dot]').forEach((item) => {
      const isActive = !!url && item.dataset.image === url;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  const bumpCartBadge = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.style.display = count ? '' : 'none';
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      const label = link.dataset.cartLabel;
      if (label) link.setAttribute('aria-label', count ? `${label} (${count})` : label);
    });
  };

  /* ---- Sepetteki adet ---------------------------------------------------
     Bir varyant birden fazla kartta görünebilir (aynı ürün hem promo kartında
     hem ızgarada). Adet TEK yerde tutulur ve o varyantın bütün formları birden
     boyanır; aksi hâlde ızgaradan eklenen ürün promo kartında hâlâ "Sepete
     Ekle" der. Kaynak her zaman sepetin kendisi — yerel sayaç tutulmaz. */
  const cartQty = new Map();
  let cartRoutes = { cart: '/cart.js', change: '/cart/change.js' };
  let cartLoad = null;

  const formVariantId = (form) => Number(form.querySelector('[name="id"]')?.value) || 0;

  const paintForm = (form) => {
    const stepper = form.querySelector('[data-cs-qty]');
    if (!stepper) return;
    const button = form.querySelector('[data-cs-add]');
    const quantity = cartQty.get(formVariantId(form)) || 0;
    /* İşlem sürerken el sürülmez: buton "Ekleniyor…" hâlindeyken sayaç
       araya girip odağı kaçırmasın. */
    if (form.dataset.csBusy) return;
    stepper.hidden = quantity < 1;
    if (button) button.hidden = quantity > 0;
    const value = stepper.querySelector('[data-cs-qty-value]');
    if (value) value.textContent = quantity;
    const status = stepper.querySelector('[data-cs-qty-status]');
    if (status) status.textContent = quantity > 0 ? `${toastState.strings.quantityLabel}: ${quantity}` : '';
    /* 1'den aşağısı "sepetten çıkar" demek; buton bunu hem ikonuyla hem
       etiketiyle söylesin. Eksi işareti orada "bir azalt" vaat ediyor, oysa
       o basış satırı tamamen siliyor. */
    const minus = stepper.querySelector('[data-cs-qty-step="-1"]');
    if (minus) {
      const removes = quantity === 1;
      if (removes) minus.title = toastState.strings.removed;
      else minus.removeAttribute('title');

      const label = removes ? minus.dataset.removeLabel : minus.dataset.decreaseLabel;
      if (label) minus.setAttribute('aria-label', label);

      /* Öznitelikle gizlenir, `.hidden` ile DEĞİL: o IDL özelliği
         HTMLElement'te tanımlı, SVGElement'te yok — atama sessizce sıradan bir
         JS alanı yazar, `[hidden]` seçicisi de eşleşmediği için iki ikon üst
         üste binerdi. */
      const setIconHidden = (icon, hide) => {
        if (!icon) return;
        if (hide) icon.setAttribute('hidden', '');
        else icon.removeAttribute('hidden');
      };
      setIconHidden(minus.querySelector('[data-cs-qty-icon="decrease"]'), removes);
      setIconHidden(minus.querySelector('[data-cs-qty-icon="remove"]'), !removes);
    }
  };

  const paintAllForms = () => document.querySelectorAll('[data-cs-form]').forEach(paintForm);

  const applyCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    cartQty.clear();
    cart.items.forEach((item) => {
      cartQty.set(item.variant_id, (cartQty.get(item.variant_id) || 0) + item.quantity);
    });
    bumpCartBadge(cart.item_count);
    paintAllForms();
  };

  const fetchCart = () => fetch(cartRoutes.cart, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => { if (cart) applyCart(cart); })
    .catch(() => { /* Sayaç görünmez kalır; sepete ekleme yolu bozulmaz. */ });

  /* Sayfa açılışında bir kez: sepette zaten olan ürün doğrudan sayaçla açılır,
     kullanıcı aynı ürünü ikinci kez eklemeye zorlanmaz. */
  const ensureCart = () => {
    if (!cartLoad) cartLoad = fetchCart();
    return cartLoad;
  };

  let addChain = Promise.resolve();

  const init = (root) => {
    if (!root || root.dataset.csInit) return;
    root.dataset.csInit = 'true';

    let strings = Object.assign({}, DEFAULT_STRINGS);
    try {
      /* Merge, atama değil: tek bir anahtar eksik olsa bile geri kalan
         metinler ayakta kalsın. */
      strings = Object.assign(strings, JSON.parse(root.querySelector('[data-cs-strings]').textContent));
    } catch (error) { /* defaults */ }
    toastState.strings = strings;

    adoptToast(root);

    const addUrl = root.dataset.cartAddUrl || '/cart/add.js';
    const cartUrl = root.dataset.cartUrl || '/cart.js';
    cartRoutes = { cart: cartUrl, change: root.dataset.cartChangeUrl || '/cart/change.js' };

    /* Sayaç taşıyan bir kart varsa sepeti bir kez oku; yoksa istek atılmaz. */
    if (root.querySelector('[data-cs-qty]')) ensureCart();

    /* Galeri: üzerine gelmek yeter; dokunmatikte pointerover dokunuşla,
       klavyede focusin ile aynı yolu tetikler. click yalnız yedek. */
    const activateDot = (dot) => {
      const card = dot.closest('[data-cs-card]');
      const image = card?.querySelector('.cs-card__image');
      if (!image || !dot.dataset.image) return;
      if (dot.classList.contains('is-active')) return;
      paintImage(image, dot);
      markActiveDot(card, dot.dataset.image);
    };

    ['pointerover', 'focusin', 'click'].forEach((type) => {
      root.addEventListener(type, (event) => {
        const dot = event.target.closest?.('[data-cs-dot]');
        if (dot) activateDot(dot);
      });
    });

    /* ---- Adet sayacı: +/- doğrudan sepet satırını değiştirir --------------
       `change.js` id olarak varyant kimliğini kabul eder; 0 satırı siler ve
       kart "Sepete Ekle" butonuna geri döner. İstekler add ile AYNI zincirde
       sırayla gider, iki tık birbirinin sonucunu ezmesin. */
    root.addEventListener('click', (event) => {
      const step = event.target.closest?.('[data-cs-qty-step]');
      if (!step) return;
      const form = step.closest('[data-cs-form]');
      const stepper = step.closest('[data-cs-qty]');
      if (!form || form.dataset.csBusy) return;

      const variantId = formVariantId(form);
      const current = cartQty.get(variantId) || 0;
      const next = Math.max(0, current + Number(step.dataset.csQtyStep || 0));
      if (!variantId || next === current) return;

      const card = form.closest('[data-cs-card]');
      const stepButtons = Array.prototype.slice.call(stepper.querySelectorAll('[data-cs-qty-step]'));
      const stepHadFocus = document.activeElement === step;
      form.dataset.csBusy = 'true';
      form.setAttribute('aria-busy', 'true');
      stepButtons.forEach((item) => { item.disabled = true; });

      addChain = addChain.catch(() => {}).then(async () => {
        try {
          const response = await fetch(cartRoutes.change, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity: next })
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || strings.error);
          }
          const cart = await response.json();
          delete form.dataset.csBusy;
          applyCart(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count, source: 'collection-storefront' } }));
        } catch (error) {
          showToast({ productTitle: card?.dataset.productTitle || '', message: error.message || strings.error }, true);
          /* Sepetin gerçek hâline dön: stok sınırına takılan bir artış
             ekranda artmış gibi kalmasın. */
          delete form.dataset.csBusy;
          cartLoad = fetchCart();
        } finally {
          delete form.dataset.csBusy;
          form.removeAttribute('aria-busy');
          stepButtons.forEach((item) => { item.disabled = false; });
          paintForm(form);
          /* Sayaç kaybolduysa (adet 0) odak butona, durduysa aynı tuşa. */
          if (stepHadFocus && document.activeElement === document.body) {
            const target = stepper.hidden ? form.querySelector('[data-cs-add]') : step;
            if (target && !target.hidden && !target.disabled) target.focus();
          }
        }
      });
    });

    root.addEventListener('change', (event) => {
      const select = event.target.closest('[data-cs-variant-select]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const card = select.closest('[data-cs-card]');
      if (!option || !card) return;

      const price = card.querySelector('[data-cs-price]');
      const compare = card.querySelector('[data-cs-compare]');
      const compareWrap = card.querySelector('[data-cs-compare-wrap]');
      const sale = card.querySelector('[data-cs-sale]');
      const image = card.querySelector('.cs-card__image');
      const button = card.querySelector('[data-cs-add]');
      const buttonLabel = card.querySelector('[data-cs-add-label]');
      const available = option.dataset.available !== 'false';

      if (price) price.textContent = option.dataset.price || '';
      if (compare) compare.textContent = option.dataset.compare || '';
      if (compareWrap) compareWrap.hidden = !option.dataset.compare;
      if (sale) sale.hidden = !option.dataset.compare;
      /* İndirim yüzdesi option'daki kuruş değerlerinden: biçimli fiyat
         metninden geri hesaplamak para birimine göre kırılgandı. */
      const discount = card.querySelector('[data-cs-discount]');
      const priceCents = Number(option.dataset.priceCents) || 0;
      const compareCents = Number(option.dataset.compareCents) || 0;
      const pct = compareCents > priceCents && compareCents > 0 ? Math.round(((compareCents - priceCents) / compareCents) * 100) : 0;
      if (discount) {
        discount.hidden = !pct;
        discount.textContent = pct ? strings.discountShort.replace('__PCT__', pct) : '';
      }
      if (sale) sale.textContent = pct ? strings.discountPercent.replace('__PCT__', pct) : strings.sale || '';
      if (button) {
        if (!available && document.activeElement === button) select.focus();
        button.disabled = !available;
      }
      if (buttonLabel) buttonLabel.textContent = available ? button?.dataset.idleLabel || strings.added : strings.soldOut;
      if (image && option.dataset.image) {
        paintImage(image, option);
        markActiveDot(card, option.dataset.image);
        /* Görsel dekoratif: medya linki aria-hidden, ürün adı başlık
           linkinde duyuruluyor. Buraya alt yazmak onu tekrar ederdi. */
        image.alt = '';
      }

      /* Adet varyanta bağlı: seçim değişince sayaç yeni varyantın sepetteki
         adedini gösterir, yoksa buton geri gelir. */
      const form = select.closest('[data-cs-form]');
      if (form) paintForm(form);
    });

    root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cs-form]');
      if (!form) return;
      event.preventDefault();
      if (form.dataset.csBusy) return;

      const button = form.querySelector('[data-cs-add]');
      const label = form.querySelector('[data-cs-add-label]');
      const select = form.querySelector('[data-cs-variant-select]');
      const card = form.closest('[data-cs-card]');
      const data = new FormData(form);
      const variantId = Number(data.get('id'));
      const selectedOption = select?.selectedOptions[0];
      if (!variantId || selectedOption?.dataset.available === 'false') return;

      toastState.lastAddButton = button;
      /* Butona basan klavye kullanıcısının odağı, buton disable edilince
         body'ye düşüyordu. İşlem bitince geri verilir. */
      const buttonHadFocus = document.activeElement === button;
      form.dataset.csBusy = 'true';
      form.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;
      if (select) select.disabled = true;
      button?.classList.add('is-busy');
      if (label) label.textContent = strings.loading;

      addChain = addChain.catch(() => {}).then(async () => {
        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || strings.error);
          }

          let cart = null;
          try {
            const cartResponse = await fetch(cartUrl, { headers: { 'Accept': 'application/json' } });
            if (cartResponse.ok) cart = await cartResponse.json();
          } catch (cartError) {
            /* The item is already in the cart; a badge refresh failure must
               not be announced as a failed add-to-cart operation. */
          }
          if (cart) applyCart(cart);
          const detail = {
            source: 'collection-storefront',
            itemCount: cart?.item_count,
            productTitle: card?.dataset.productTitle || '',
            variantTitle: selectedOption?.dataset.variantTitle || '',
            quantity: 1,
            message: strings.added,
            items: [{
              id: variantId,
              productTitle: card?.dataset.productTitle || '',
              variantTitle: selectedOption?.dataset.variantTitle || '',
              quantity: 1
            }]
          };
          if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count, source: 'collection-storefront' } }));
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail }));
          if (label) label.textContent = strings.added;
          /* Onay hâli: spinner kapanır, buton turuncu kalır. */
          button?.classList.replace('is-busy', 'is-added');

          /* Sayaçlı kart: buton beklemeden sayaca döner. Butona basan klavye
             kullanıcısının odağı "+" tuşuna geçer, body'ye düşmez. */
          const stepper = form.querySelector('[data-cs-qty]');
          if (stepper && cart) {
            delete form.dataset.csBusy;
            paintForm(form);
            if (buttonHadFocus && !stepper.hidden) {
              stepper.querySelector('[data-cs-qty-step="1"]')?.focus();
            }
          }
        } catch (error) {
          showToast({ productTitle: card?.dataset.productTitle || '', message: error.message || strings.error }, true);
          if (label) label.textContent = strings.error;
        } finally {
          setTimeout(() => {
            delete form.dataset.csBusy;
            form.removeAttribute('aria-busy');
            button?.classList.remove('is-busy', 'is-added');
            if (select) select.disabled = false;
            const currentOption = select?.selectedOptions[0];
            const currentAvailable = currentOption?.dataset.available !== 'false';
            if (button) button.disabled = !currentAvailable;
            if (label) label.textContent = currentAvailable ? button?.dataset.idleLabel || strings.added : strings.soldOut;
            paintForm(form);
            if (buttonHadFocus && button && !button.hidden && !button.disabled && document.activeElement === document.body) {
              button.focus();
            }
          }, 1800);
        }
      });
    });
  };

  /* Sepeti bu dosyadan başkası da değiştiriyor (sihirbazın "Hepsini sepete
     ekle"si, sepet sayfası, üstteki sepet). O olayları dinleyip adetleri
     sepetten yeniden okuruz; kendi olaylarımız zaten uygulanmış durumda. */
  ['cart:item-added', 'cart:updated'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if ((event.detail || {}).source === 'collection-storefront') return;
      if (!document.querySelector('[data-cs-qty]')) return;
      cartLoad = fetchCart();
    });
  });

  const initAll = () => document.querySelectorAll('[data-cs-root]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  /* Theme editor bir bölümü yeniden bastığında yeni kök gelir. */
  document.addEventListener('shopify:section:load', initAll);
})();
